import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// Component for viewing and managing slides/images with drag-and-drop reordering
const ImageViewer = ({ imageArray, activeSlide, slidesConfig, setSlidesConfig, onDragEnd, setActiveSlide, controls, setImageArray }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Handle thumbnail click to change active slide
  const handleThumbnailClick = (index) => {
    console.info('Changing active slide to', index + 1);
    setActiveIndex(index);
    setActiveSlide(index + 1);
  };

  // Handle drag and drop reordering of slides
  const handleDragEnd = (result) => {
    console.info('Handling slide reorder');
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if(sourceIndex === destinationIndex) return;

    const newSlidesConfig = Array.from(slidesConfig);
    const [reorderedItem] = newSlidesConfig.splice(sourceIndex, 1);
    newSlidesConfig.splice(destinationIndex, 0, reorderedItem);

    const newImageArray = Array.from(imageArray);
    const [reorderedImage] = newImageArray.splice(sourceIndex, 1);
    newImageArray.splice(destinationIndex, 0, reorderedImage);

    setSlidesConfig(newSlidesConfig);
    setImageArray(newImageArray);
    onDragEnd(newSlidesConfig);

    if (activeIndex === sourceIndex) {
      setActiveIndex(destinationIndex);
      setActiveSlide(destinationIndex + 1);
    }
  };

  return (
    <div className="flex h-[90vh] gap-4 w-full">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="thumbnails">
          {(provided) => (
            <div 
              className="w-1/5 overflow-y-auto bg-white"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {imageArray.length > 0 ? (
                imageArray.map((image, index) => (
                  <Draggable key={index} draggableId={`slide-${index}`} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => handleThumbnailClick(index)}
                        className={`p-4 cursor-pointer m-2 h-[200px] ${
                          activeIndex === index ? "border-l-4 border-blue-500" : ""
                        }`}
                      >
                        <div className="flex gap-2 -ml-3">
                          <span className="-mt-4">{index + 1}</span>
                          <img
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-[190px] object- -mt-3 rounded shadow-lg"
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))
              ) : (
                <p className="p-4 text-gray-500">No images available</p>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      
      <div className="flex-1 flex flex-col p-8 bg-[#f7f8ff]">
        <div className="h-[56px] bg-white flex justify-center items-center">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => {
                console.info('Moving to previous slide');
                if (activeIndex >= 1) {
                  setActiveSlide(activeIndex-1);
                  setActiveIndex(activeIndex - 1);
                }
              }}
              className={`text-gray-600 hover:text-black ${
                activeIndex === 0 && "opacity-50 cursor-not-allowed"
              }`}
              disabled={activeIndex+1 === 1}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            <span className="text-gray-800 font-medium">
              Slide {activeIndex+1}/{imageArray?.length}
            </span>
            <button
              onClick={() => {
                console.info('Moving to next slide');
                if (activeSlide < imageArray.length) {
                  setActiveSlide(activeIndex+1);
                  setActiveIndex(activeIndex + 1);
                }
              }}
              className={`text-gray-600 hover:text-black ${
                activeIndex+1 === imageArray.length &&
                "opacity-50 cursor-not-allowed"
              }`}
              disabled={activeIndex+1 === imageArray.length}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        </div>
        {imageArray[activeIndex] ? (
          <img
            src={imageArray[activeIndex]}
            alt={`Full view ${activeIndex + 1}`}
            className="max-w-full h-[62vh] object-contain rounded shadow-lg"
          />
        ) : (
          <p className="text-gray-500">Select an image to view</p>
        )}

        <div className="mt-4">{controls}</div>
      </div>
    </div>
  );
};

export default ImageViewer;
